import { z } from 'zod'
import type { PaginatedResponse } from './common'

export const donationStatusSchema = z.enum([
  'created',
  'paid',
  'failed',
  'completed',
])
export type DonationStatus = z.infer<typeof donationStatusSchema>

export const donationAmountSchema = z.number().int().min(100).max(100_000_000)

const panNumberRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/
const aadhaarNumberRegex = /^[0-9]{12}$/
const pinCodeRegex = /^[0-9]{6}$/
const phoneDigitsRegex = /^[0-9]{6,15}$/

function emptyToUndefined(value: unknown) {
  if (value === '' || value === null || value === undefined) return undefined
  return value
}

export const donationDonorDetailsSchema = z
  .object({
    first_name: z.string().trim().min(1).max(100),
    last_name: z.string().trim().min(1).max(100),
    email: z.string().trim().email().max(255),
    phone_country_code: z.string().trim().min(1).max(6),
    phone: z.string().trim().regex(phoneDigitsRegex),
    is_indian_citizen: z.boolean(),
    wants_80g_benefit: z.boolean().default(false),
    country: z.string().trim().min(1).max(100),
    state: z.string().trim().min(1).max(100),
    city: z.string().trim().min(1).max(100),
    address: z.string().trim().min(10).max(500),
    pin_code: z.string().trim().min(3).max(12),
    occasion: z.preprocess(
      emptyToUndefined,
      z.string().trim().max(100).optional(),
    ),
    occasion_date: z.preprocess(emptyToUndefined, z.string().date().optional()),
    persons_to_honour: z.preprocess(
      emptyToUndefined,
      z.string().trim().max(200).optional(),
    ),
    pan_number: z.preprocess(
      emptyToUndefined,
      z
        .string()
        .trim()
        .transform(v => v.toUpperCase())
        .optional(),
    ),
    no_pan: z.boolean().default(false),
    aadhaar_number: z.preprocess(
      emptyToUndefined,
      z.string().trim().optional(),
    ),
  })
  .superRefine((value, ctx) => {
    if (!value.is_indian_citizen) {
      if (value.wants_80g_benefit) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['wants_80g_benefit'],
          message: '80G benefit is available only for Indian citizens',
        })
      }
      return
    }

    if (value.country.trim().toLowerCase() !== 'india') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['country'],
        message: 'Country must be India for Indian citizens',
      })
    }

    if (!pinCodeRegex.test(value.pin_code)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pin_code'],
        message: 'Indian pin code must be 6 digits',
      })
    }

    if (!value.wants_80g_benefit) return

    if (value.no_pan) {
      if (
        !value.aadhaar_number ||
        !aadhaarNumberRegex.test(value.aadhaar_number)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['aadhaar_number'],
          message: 'Aadhaar number must be 12 digits when PAN is unavailable',
        })
      }
      return
    }

    if (!value.pan_number || !panNumberRegex.test(value.pan_number)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pan_number'],
        message: 'PAN number format is invalid',
      })
    }
  })
export type DonationDonorDetailsInput = z.infer<
  typeof donationDonorDetailsSchema
>

const createDonationBaseSchema = z.object({
  campaign_id: z.string().optional(),
  donor_name: z.string().max(200).optional(),
  amount_paise: donationAmountSchema,
  currency: z.string().min(3).max(10).default('INR'),
  payment_id: z.string().max(255).optional(),
  order_id: z.string().max(255).optional(),
  status: donationStatusSchema.default('completed'),
  is_anonymous: z.boolean().default(false),
  receipt_no: z.string().max(100).optional(),
  donated_at: z.string().datetime().optional(),
})

export const createDonationSchema = createDonationBaseSchema
export type CreateDonationInput = z.infer<typeof createDonationSchema>

export const createDonationOrderSchema = z.object({
  campaign_id: z.string().min(1),
  donor_name: z.string().max(200).optional(),
  amount_paise: donationAmountSchema,
  is_anonymous: z.boolean().default(false),
  donor_details: donationDonorDetailsSchema,
  terms_accepted: z.literal(true),
})
export type CreateDonationOrderInput = z.infer<typeof createDonationOrderSchema>

export const verifyDonationPaymentSchema = z.object({
  donation_id: z.string().min(1),
  razorpay_order_id: z.string().min(1),
  razorpay_payment_id: z.string().min(1),
  razorpay_signature: z.string().min(1),
})
export type VerifyDonationPaymentInput = z.infer<
  typeof verifyDonationPaymentSchema
>

export type DonationResponse = {
  id: string
  legacy_id?: string | null
  user_email?: string | null
  campaign_id?: string | null
  donor_name?: string | null
  campaign_title_en?: string | null
  donor_first_name?: string | null
  donor_last_name?: string | null
  donor_email?: string | null
  donor_phone_country_code?: string | null
  donor_phone?: string | null
  is_indian_citizen?: boolean | null
  wants_80g_benefit?: boolean | null
  donor_country?: string | null
  donor_state?: string | null
  donor_city?: string | null
  donor_address?: string | null
  donor_pin_code?: string | null
  occasion?: string | null
  occasion_date?: string | null
  persons_to_honour?: string | null
  pan_number?: string | null
  no_pan?: boolean | null
  aadhaar_number?: string | null
  terms_accepted_at?: string | null
  amount_paise: number
  currency: string
  payment_id?: string | null
  order_id?: string | null
  status: DonationStatus
  is_anonymous: boolean
  receipt_no?: string | null
  donated_at?: string | null
  created_at: string
  updated_at: string
}

export type DonationSortBy = 'created_at' | 'donated_at' | 'amount_paise'

export type PaginatedDonationResponse = PaginatedResponse<DonationResponse>

export type DonationOrderResponse = {
  donation_id: string
  order_id: string
  amount: number
  amount_paise: number
  currency: string
  razorpay_key: string
}
